import config from '../config';
import { isClinicalRecoveryWrite, requiresConfirmedWrite } from './config';
import { getAuthHeaders } from '../utils/api/common';

const PREGNANCY_CHART_PATTERN = /^\/api\/pregnancies\/([^/]+)\/(prenatal|postpartum|newborn|lactation)-visits(?:\/([^/]+))?$/;
const WELL_WOMAN_PATTERN = /^\/api\/clients\/([^/]+)\/well-woman-visits(?:\/([^/]+))?$/;

const readBody = (body) => {
  try { return JSON.parse(body || '{}'); } catch { return {}; }
};

const firstValue = (record, keys) => {
  for (const key of keys) {
    const value = record?.[key];
    if (value !== null && value !== undefined && String(value).trim()) return value;
  }
  return null;
};

export function inspectLegacyClinicalRequest(item) {
  if (!item?.url || !['POST', 'PUT', 'PATCH', 'DELETE'].includes(String(item.method || '').toUpperCase())) return null;
  let pathname;
  try { pathname = new URL(item.url, window.location.origin).pathname; } catch { return null; }
  const pregnancyMatch = pathname.match(PREGNANCY_CHART_PATTERN);
  const wellWomanMatch = pathname.match(WELL_WOMAN_PATTERN);
  if (!pregnancyMatch && !wellWomanMatch) {
    const method = String(item.method).toUpperCase();
    if (!isClinicalRecoveryWrite(pathname, method) && !requiresConfirmedWrite(pathname, method)) return null;
    const pregnancy = pathname.match(/^\/api\/pregnancies\/([^/]+)/)?.[1];
    const client = pathname.match(/^\/api\/clients\/([^/]+)/)?.[1];
    return { ...item, method, chartType: 'retained-chart-change', recoveryOnly: true,
      pregnancyIdentifier: pregnancy || null, pregnancyReferenceId: pregnancy || null,
      clientReferenceId: client || null, chartId: null };
  }

  const body = readBody(item.body);
  const chartType = pregnancyMatch?.[2] || 'well-woman';
  return {
    ...item,
    method: String(item.method).toUpperCase(),
    chartType,
    pregnancyIdentifier: pregnancyMatch?.[1] || null,
    pregnancyReferenceId: pregnancyMatch?.[1] || null,
    clientReferenceId: wellWomanMatch?.[1] || null,
    chartId: pregnancyMatch?.[3] || wellWomanMatch?.[2] || null,
    visitDate: firstValue(body, ['VisitDate', 'visitDate', 'EncounterDate', 'encounterDate', 'Date', 'date']),
    baseVersion: firstValue(body, ['UpdatedAt', 'updatedAt']),
  };
}

export async function resolveLegacyClinicalContext(item) {
  const inspected = inspectLegacyClinicalRequest(item);
  if (!inspected) throw new Error('This queued operation is not a supported clinical chart.');
  if (inspected.clientReferenceId || !inspected.pregnancyIdentifier) return inspected;

  const response = await fetch(`${config.api.url}/pregnancies/${encodeURIComponent(inspected.pregnancyIdentifier)}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Could not identify the client for this chart (HTTP ${response.status}).`);
  const pregnancy = await response.json();
  const clientReferenceId = pregnancy.clientReferenceId || pregnancy.ClientReferenceId;
  const pregnancyReferenceId = pregnancy.referenceId || pregnancy.ReferenceId || inspected.pregnancyIdentifier;
  if (!clientReferenceId) throw new Error('The server did not return the client reference for this pregnancy.');
  return {
    ...inspected,
    clientReferenceId,
    pregnancyReferenceId,
    pregnancyId: pregnancy.id || pregnancy.Id || null,
  };
}
